import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import useAlertProvider from '../components/alert/useAlertProvider';
import { TECH_ERROR_DESC, TECH_ERROR_TITLE } from '../lib/constants/lookupErrors';
import { LOCKED_ROUTE, MULTIPLE_MATCHES_ROUTE, REGISTRATION_BLOCKED_ROUTE } from '../lib/constants/routes';
import { apiErrorAnalytics, tags } from '../lib/utils/analytics';
import { isCaremark, isLocal, isSpecialty, submitForm } from '../lib/utils/utils';
import { APIROUTEPREFIX } from '../lib/constants/apiAttributes';
import UserContext from '../context/UserContext';
import { phoneDOBErrorDesc, phoneDOBErrorRemainingAttempts } from '@/components/errorMessages/errorMessages';
import { LOOKUP_ERRORS } from '@/lib/constants/phoneDob';
import { XROUTEHDR } from '@/lib/constants/apiAttributes';
import { useLookupErrorsTranslations, usePhoneDobTranslations } from '../lib/utils/translationHelpers';

const guestPhoneDOBLookupMutation = (formData: any, campaignInfo: any) => {
  const { lookupData, updateLookupData } = UserContext();
  const api = lookupData?.phoneDOBInfoFormFlag ? '/api/guestPhoneDOBLookup' : '/api/guestRxLabelLookup';
  const apiRoute = !isLocal() ? APIROUTEPREFIX + api : api;
  const { setAlerts } = useAlertProvider();
  const { push, query } = useRouter();
  let showError = false;
  const LOOKUP_ERRORS_TRANSLATED = useLookupErrorsTranslations();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch(apiRoute, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-route': XROUTEHDR.gkeAndRke
        },
        body: JSON.stringify(formData)
      });
      return await response.json();
    },
    onMutate: () => {
      setAlerts([]);
    },
    onSuccess: async (res: any) => {
      const data = res && res?.data;
      const statusCode = res && res?.statusCode.toString();
      let errTitle = '';
      let errDesc: any = '';
      let error = false;
      let info = false;
      let techError = false;
      let analyticsErrorTitle = ''; // Track English constant for analytics

      if (statusCode === null || statusCode === undefined) {
        error = true;
        errTitle = LOOKUP_ERRORS_TRANSLATED?.TECH_ERROR_TITLE;
        errDesc = LOOKUP_ERRORS_TRANSLATED?.TECH_ERROR_DESC;
        techError = true;
        analyticsErrorTitle = TECH_ERROR_TITLE;
        apiErrorAnalytics(formData?.flow, formData?.pageTag, analyticsErrorTitle);
      } else {
        switch (statusCode.toString()) {
          //Success scenarios
          case '0000': {
            const { lookupToken } = data?.response;
            lookupToken && submitForm(JSON.stringify({ ...campaignInfo, lookupToken }));
            break;
          }
          case '9999':
          case '5001': {
            const fault = res && res?.fault;
            const errorType = fault?.errors[0]?.type;
            switch (errorType) {
              case 'LOOKUP_NO_DATA_FOUND.2':
              case 'LOOKUP_NO_DATA_FOUND.1': {
                //EPH match not found, attempts remaining
                const remainingAttempts = errorType[errorType.length - 1];
                const errorObj =
                  remainingAttempts === '2'
                    ? LOOKUP_ERRORS.two_attempts_remaining
                    : LOOKUP_ERRORS.one_attempt_remaining;
                errTitle = errorObj.title;
                errDesc =
                  !isSpecialty() && !isCaremark()
                    ? phoneDOBErrorRemainingAttempts(
                        errorObj.description,
                        lookupData,
                        updateLookupData,
                        usePhoneDobTranslations()
                      )
                    : errorObj.description;
                analyticsErrorTitle = errorObj.title;
                error = true;
                info = true;
                break;
              }
              case 'LOOKUP_NO_DATA_FOUND.0':
              case 'LOOKUP_LOCKED_MOBILE':
              case 'LOOKUP_LOCKED_RX': {
                //user is out of attempts, or already locked
                push({
                  pathname: LOCKED_ROUTE + '/lockedFor',
                  query: { lockedFor: 'identity-search', isGuest: 'true', ...query }
                });
                const errorObj = LOOKUP_ERRORS.locked_error;
                error = true;
                errTitle = errorObj.title;
                errDesc = phoneDOBErrorDesc(errorObj.description);
                analyticsErrorTitle = errorObj.title;
                break;
              }
              case 'CMK_REG_BLOCKED': {
                //Registration blocked
                error = true;
                push(
                  {
                    pathname: REGISTRATION_BLOCKED_ROUTE,
                    query: { title: LOOKUP_ERRORS.registration_blocked.title, message: fault?.errors[0]?.title }
                  },
                  REGISTRATION_BLOCKED_ROUTE
                );
                break;
              }
              case 'LOOKUP_NO_UNIQUE_DATA_FOUND_MOBILE':
              case 'LOOKUP_MULTI_CVS_ID_WITH_PHONE_DOB':
              case 'LOOKUP_MULTIPLE_ACTIVE_PLANS_FOUND':
              case 'LOOKUP_NO_ACTIVE_PLANS_FOUND':
              case 'LOOKUP_BLANK_BEN_ID_FOUND':
              case 'LOOKUP_BEN_ID_MISMATCH':
              case 'LOOKUP_CVS_ID_MISSING': {
                push({
                  pathname: MULTIPLE_MATCHES_ROUTE
                });
                break;
              }
              default: {
                techError = true;
                errTitle = LOOKUP_ERRORS.tech_error.title;
                errDesc = phoneDOBErrorDesc(LOOKUP_ERRORS.tech_error.description);
                analyticsErrorTitle = LOOKUP_ERRORS.tech_error.title;
                break;
              }
            }
            apiErrorAnalytics(lookupData?.appProps?.adobeFlowName, formData?.pageTag, analyticsErrorTitle);
            break;
          }
          default: {
            //tech err
            error = true;
            errTitle = LOOKUP_ERRORS_TRANSLATED?.TECH_ERROR_TITLE;
            errDesc = LOOKUP_ERRORS_TRANSLATED?.TECH_ERROR_DESC;
            analyticsErrorTitle = TECH_ERROR_TITLE;
            apiErrorAnalytics(lookupData?.appProps?.adobeFlowName, formData?.pageTag, analyticsErrorTitle);
            break;
          }
        }
      }

      //ERRORS
      if (error === true || techError === true) {
        showError = true;
        if (errTitle === '' && errDesc === '') {
          errTitle = LOOKUP_ERRORS.tech_error.title;
          errDesc = phoneDOBErrorDesc(LOOKUP_ERRORS.tech_error.description);
          analyticsErrorTitle = LOOKUP_ERRORS.tech_error.title;
        }
        apiErrorAnalytics(tags.selfServe, tags.phoneDOB, analyticsErrorTitle);
        setTimeout(() => {
          setAlerts([
            {
              title: errTitle,
              message: errDesc,
              info
            }
          ]);
        }, 10);
      }
    },
    onError: async (error: any) => {
      !showError &&
        setTimeout(() => {
          setAlerts([
            {
              title: LOOKUP_ERRORS_TRANSLATED?.TECH_ERROR_TITLE,
              message: LOOKUP_ERRORS_TRANSLATED?.TECH_ERROR_DESC
            }
          ]);
        }, 10);
      apiErrorAnalytics(lookupData?.appProps?.adobeFlowName, formData?.pageTag, TECH_ERROR_TITLE);
    }
  });
};

export default guestPhoneDOBLookupMutation;
